import type { EvidiaModalProps } from '@evidia/components';
import { EvidiaModal, EvidiaStaticModalType } from '@evidia/components';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

export type EvidiaStaticModalProps = Pick<EvidiaModalProps, 'onlyCloseButton'> & {
	title?: ReactNode;
	content?: ReactNode;
	onOk?: () => void;
	onCancel?: () => void;
};

type EvidiaStaticModalState = EvidiaStaticModalProps & {
	staticType: EvidiaStaticModalType;
};

type StaticModalContextType = Record<EvidiaStaticModalType, (params: EvidiaStaticModalProps) => void>;

const StaticModalContext = createContext<StaticModalContextType | null>(null);

type Props = {
	children: ReactNode;
};

export const StaticModalProvider = ({ children }: Props) => {
	const [staticModalState, setStaticModalState] = useState<EvidiaStaticModalState | null>(null);

	const modalCallback = useMemo(
		() => ({
			info: (props: EvidiaStaticModalProps) => {
				setStaticModalState({
					...props,
					staticType: EvidiaStaticModalType.INFO,
				});
			},
			success: (props: EvidiaStaticModalProps) => {
				setStaticModalState({
					...props,
					staticType: EvidiaStaticModalType.SUCCESS,
				});
			},
			warning: (props: EvidiaStaticModalProps) => {
				setStaticModalState({
					...props,
					staticType: EvidiaStaticModalType.WARNING,
				});
			},
			error: (props: EvidiaStaticModalProps) => {
				setStaticModalState({
					...props,
					staticType: EvidiaStaticModalType.ERROR,
				});
			},
		}),
		[],
	);

	return (
		<StaticModalContext.Provider value={modalCallback}>
			{children}
			{staticModalState && (
				<EvidiaModal
					{...staticModalState}
					open={Boolean(staticModalState)}
					onCancel={() => {
						staticModalState.onCancel?.();
						setStaticModalState(null);
					}}
					onOk={() => {
						staticModalState.onOk?.();
						setStaticModalState(null);
					}}
				>
					{staticModalState.content}
				</EvidiaModal>
			)}
		</StaticModalContext.Provider>
	);
};

export const useStaticModal = () => {
	const modal = useContext(StaticModalContext);

	if (!modal) {
		throw new Error('useStaticModal must be used within a StaticModalProvider');
	}

	return modal;
};
